import { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  ChevronLeft,
  AlertCircle,
  CheckCircle2,
  UserCheck,
  UserPlus,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { pickFormId, isSponsorRequired, sponsorRoleNoun } from '../lib/schema-runtime';
import { seedAgentPrefill } from '../lib/agentPrefill';
import { relationshipStatusForVariant } from '../lib/relationshipBasis';
import { useMatter } from '../hooks/useMatter';

interface StaffOption {
  id: string;
  given_names: string | null;
  family_name: string | null;
  email: string;
}

interface ResolvedContact {
  id: string;
  email: string;
  given_names: string | null;
  family_name: string | null;
  phone: string | null;
  contact_type: string;
  active_matter_count: number;
}

// `sponsorLabel` = the heading of the second-party block, or null when the
// subclass has no second party at all. Whether that party is MANDATORY is a
// separate question, answered by isSponsorRequired(). The 600 tourist stream
// has an OPTIONAL Inviter, so it carries a label but is never gated on
// (Tzvi ruling 2026-07-24, Wave 5).
const VISA_OPTIONS: Array<{
  value: string;
  label: string;
  variants: string[];
  sponsorLabel: string | null;
  sponsorHelper?: string;
}> = [
  { value: '820', label: '820 - Partner (onshore, temporary)', variants: ['defacto', 'married'], sponsorLabel: 'Sponsor (Australian partner)' },
  { value: '801', label: '801 - Partner (onshore, permanent)', variants: ['defacto', 'married'], sponsorLabel: 'Sponsor (Australian partner)' },
  { value: '309', label: '309 - Partner (offshore, provisional)', variants: ['defacto', 'married'], sponsorLabel: 'Sponsor (Australian partner)' },
  { value: '100', label: '100 - Partner (offshore, permanent)', variants: ['defacto', 'married'], sponsorLabel: 'Sponsor (Australian partner)' },
  { value: '300', label: '300 - Prospective Marriage (PMV)', variants: ['engaged'], sponsorLabel: 'Sponsor (Australian fiancé/e)' },
  {
    value: '600-tourist',
    label: '600 - Visitor (Tourist stream)',
    variants: ['onshore', 'offshore'],
    sponsorLabel: 'Inviter (optional)',
    sponsorHelper:
      'If someone in Australia is inviting, hosting or supporting this visitor, add them here and they will get their own login.',
  },
  { value: '600-family', label: '600 - Visitor (Family Sponsored)', variants: ['offshore'], sponsorLabel: 'Australian sponsor (relative)' },
  { value: '461', label: '461 - NZ Family Relationship', variants: ['onshore', 'offshore'], sponsorLabel: 'NZ citizen family member' },
  { value: '020', label: '020 - Bridging visa B (BVB)', variants: ['onshore'], sponsorLabel: null },
];

export default function CreateMatter() {
  const navigate = useNavigate();
  const { contact, isStaff, refresh, setActiveMatterId } = useMatter();
  const [searchParams] = useSearchParams();
  const prefilledContactId = searchParams.get('contact');

  // --- Client (applicant) state ---
  const [clientEmail, setClientEmail] = useState('');
  const [clientGivenNames, setClientGivenNames] = useState('');
  const [clientFamilyName, setClientFamilyName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientResolved, setClientResolved] = useState<ResolvedContact | null>(null);
  const [clientLookupState, setClientLookupState] = useState<'idle' | 'searching' | 'found' | 'new'>('idle');

  // --- Sponsor state (mirrors client) ---
  const [sponsorEmail, setSponsorEmail] = useState('');
  const [sponsorGivenNames, setSponsorGivenNames] = useState('');
  const [sponsorFamilyName, setSponsorFamilyName] = useState('');
  const [sponsorPhone, setSponsorPhone] = useState('');
  const [sponsorResolved, setSponsorResolved] = useState<ResolvedContact | null>(null);
  const [sponsorLookupState, setSponsorLookupState] = useState<'idle' | 'searching' | 'found' | 'new'>('idle');

  const [visaSubclass, setVisaSubclass] = useState('820');
  const [variant, setVariant] = useState('defacto');
  const [assignedStaffId, setAssignedStaffId] = useState<string>('');
  const [initialPaymentDate, setInitialPaymentDate] = useState<string>(today());
  const [internalNotes, setInternalNotes] = useState('');

  const [staffList, setStaffList] = useState<StaffOption[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const visaOption = VISA_OPTIONS.find((v) => v.value === visaSubclass) ?? VISA_OPTIONS[0];

  useEffect(() => {
    (async () => {
      const { data, error: err } = await supabase
        .from('contacts')
        .select('id, given_names, family_name, email')
        .eq('contact_type', 'staff')
        .is('deleted_at', null)
        .order('given_names', { ascending: true });
      if (err) {
        console.error('Failed to load staff list', err);
        return;
      }
      setStaffList((data ?? []) as StaffOption[]);
    })();
  }, []);

  useEffect(() => {
    if (contact && !assignedStaffId) {
      setAssignedStaffId(contact.id);
    }
  }, [contact, assignedStaffId]);

  useEffect(() => {
    if (!visaOption.variants.includes(variant)) {
      setVariant(visaOption.variants[0]);
    }
  }, [visaSubclass, visaOption.variants, variant]);

  // Pre-lock client from /matters/new?contact=ID (used by "New matter for this client" on profile page).
  useEffect(() => {
    if (!prefilledContactId) return;
    let cancelled = false;
    (async () => {
      const resolved = await loadResolvedContact(prefilledContactId);
      if (cancelled || !resolved) return;
      setClientResolved(resolved);
      setClientEmail(resolved.email);
      setClientGivenNames(resolved.given_names ?? '');
      setClientFamilyName(resolved.family_name ?? '');
      setClientPhone(resolved.phone ?? '');
      setClientLookupState('found');
    })();
    return () => {
      cancelled = true;
    };
  }, [prefilledContactId]);

  // Debounced email lookups (300ms) for both client and sponsor sections.
  useDebouncedLookup(clientEmail, prefilledContactId ? null : setClientResolved, prefilledContactId ? null : setClientLookupState, clientResolved);
  useDebouncedLookup(sponsorEmail, setSponsorResolved, setSponsorLookupState, sponsorResolved);

  if (!isStaff) {
    return (
      <div className="card p-8 text-center">
        <AlertCircle className="mx-auto mb-3 text-slate-400" size={32} />
        <h2 className="text-lg font-semibold mb-1">Staff access only</h2>
        <p className="text-sm text-slate-600 mb-4">Creating matters is for ADI staff.</p>
        <Link to="/dashboard" className="btn-primary inline-flex">Go to dashboard</Link>
      </div>
    );
  }

  // The client must be identified (either an existing contact or new with at least name fields)
  // before the rest of the form is meaningful. Submit is gated on this too.
  const clientIdentified =
    !!clientResolved ||
    (clientEmail.trim().length > 0 &&
      clientGivenNames.trim().length > 0 &&
      clientFamilyName.trim().length > 0 &&
      clientLookupState === 'new');

  // Whether the second party is MANDATORY. Was `!!visaOption.sponsorLabel`,
  // which conflated "this subclass shows a second-party block" with "you may
  // not create the matter without one". The 600 tourist Inviter shows a block
  // but is optional, so requiredness comes from isSponsorRequired(), the same
  // helper MatterReadiness uses. Mirror the client gate: an existing contact
  // counts, or a new one with name + email filled. Optional-party visas leave
  // this true so submit is never blocked.
  const sponsorRequired = isSponsorRequired(visaSubclass);
  const sponsorNoun = sponsorRoleNoun(visaSubclass);
  const sponsorIdentified =
    !sponsorRequired ||
    !!sponsorResolved ||
    (sponsorEmail.trim().length > 0 &&
      sponsorGivenNames.trim().length > 0 &&
      sponsorFamilyName.trim().length > 0 &&
      sponsorLookupState === 'new');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const trimmedEmail = clientEmail.trim().toLowerCase();
      if (!trimmedEmail) throw new Error('Client email is required.');
      if (!EMAIL_RE.test(trimmedEmail)) {
        throw new Error('That client email does not look right. Check for typos before continuing (e.g. "outlook.com", not "ooutlook.com").');
      }
      if (clientPhone.trim() && !PHONE_RE.test(clientPhone.trim())) {
        throw new Error('That client phone number does not look right. Use digits with an optional leading + (e.g. +61 412 345 678).');
      }
      if (!clientIdentified) throw new Error('Identify the client first (existing or new).');

      // Resolve client contact id. Existing contacts use their id; new ones get inserted.
      let primaryContactId: string;
      if (clientResolved) {
        primaryContactId = clientResolved.id;
      } else {
        // Race-safety: re-check by email at submit time before insert.
        const { data: existing } = await supabase
          .from('contacts')
          .select('id')
          .eq('email', trimmedEmail)
          .maybeSingle();
        if (existing) {
          primaryContactId = existing.id;
        } else {
          const { data: created, error: createErr } = await supabase
            .from('contacts')
            .insert({
              email: trimmedEmail,
              given_names: clientGivenNames.trim() || null,
              family_name: clientFamilyName.trim() || null,
              phone: clientPhone.trim() || null,
              contact_type: 'applicant',
            })
            .select('id')
            .single();
          if (createErr) throw createErr;
          primaryContactId = created.id;
        }
      }

      // Second party. Mandatory for sponsor-required visas (820/801/309/100/300,
      // 600-family, 461); optional for the 600 tourist Inviter and absent
      // entirely for 020. When optional, leaving the whole block blank creates
      // the matter with no second party at all (and fires no welcome email);
      // filling ANY of it commits to a full contact, because contacts.email is
      // NOT NULL and the party needs an email to receive their own login.
      let sponsorContactId: string | null = null;
      const trimmedSponsorEmail = sponsorEmail.trim().toLowerCase();
      const sponsorPartiallyFilled =
        !!sponsorResolved ||
        !!trimmedSponsorEmail ||
        !!sponsorGivenNames.trim() ||
        !!sponsorFamilyName.trim() ||
        !!sponsorPhone.trim();
      if (sponsorRequired) {
        if (!trimmedSponsorEmail) throw new Error(`${capitalise(sponsorNoun)} email is required for this visa.`);
        if (!sponsorResolved && (!sponsorGivenNames.trim() || !sponsorFamilyName.trim())) {
          throw new Error(`${capitalise(sponsorNoun)} given names and family name are required for this visa.`);
        }
      } else if (sponsorPartiallyFilled) {
        if (!trimmedSponsorEmail) {
          throw new Error(
            `An email is needed for the ${sponsorNoun}, because they get their own portal login. Add one, or clear the ${sponsorNoun} details to create the matter without one.`,
          );
        }
        if (!sponsorResolved && (!sponsorGivenNames.trim() || !sponsorFamilyName.trim())) {
          throw new Error(`${capitalise(sponsorNoun)} given names and family name are required.`);
        }
      }
      if (sponsorRequired || sponsorPartiallyFilled) {
        if (!EMAIL_RE.test(trimmedSponsorEmail)) {
          throw new Error(`That ${sponsorNoun} email does not look right. Check for typos before continuing.`);
        }
        if (sponsorPhone.trim() && !PHONE_RE.test(sponsorPhone.trim())) {
          throw new Error(`That ${sponsorNoun} phone number does not look right. Use digits with an optional leading +.`);
        }
        if (sponsorResolved) {
          sponsorContactId = sponsorResolved.id;
        } else {
          const { data: existingSponsor } = await supabase
            .from('contacts')
            .select('id')
            .eq('email', trimmedSponsorEmail)
            .maybeSingle();
          if (existingSponsor) {
            sponsorContactId = existingSponsor.id;
          } else {
            const { data: newSponsor, error: newSponsorErr } = await supabase
              .from('contacts')
              .insert({
                email: trimmedSponsorEmail,
                given_names: sponsorGivenNames.trim() || null,
                family_name: sponsorFamilyName.trim() || null,
                phone: sponsorPhone.trim() || null,
                contact_type: 'sponsor',
              })
              .select('id')
              .single();
            if (newSponsorErr) throw newSponsorErr;
            sponsorContactId = newSponsor.id;
          }
        }
      }

      const { data: matter, error: matterErr } = await supabase
        .from('matters')
        .insert({
          visa_subclass: visaSubclass,
          variant: variant,
          // Matter-level relationship status (R5): set from the chosen variant
          // when it is a partner basis, so the client's locked intake display
          // and the appears_when gates bind to the matter value from day one.
          // Engaged (300 PMV) and non-partner variants leave it NULL.
          relationship_status: relationshipStatusForVariant(variant),
          status: 'In Progress',
          primary_contact_id: primaryContactId,
          sponsor_contact_id: sponsorContactId,
          assigned_staff_id: assignedStaffId || null,
          initial_payment_date: initialPaymentDate || null,
          // Staff-only note (B13/S2 fix). Column is nullable and staff-write-
          // only by app convention — see the migration comment for why it's
          // never selected on a client-reachable query.
          internal_notes: internalNotes.trim() || null,
        })
        .select('id')
        .single();
      if (matterErr) throw matterErr;

      const { form_code, form_version } = pickIntakeForm(visaSubclass);
      const { data: intakeForm, error: intakeErr } = await supabase
        .from('intake_forms')
        .insert({
          matter_id: matter.id,
          contact_id: primaryContactId,
          form_code,
          form_version,
          status: 'in_progress',
        })
        .select('id')
        .single();
      if (intakeErr) {
        console.warn('Intake form row creation failed (matter still created):', intakeErr);
      } else if (intakeForm) {
        // Seed ADI's own authorised-recipient details. The client never sees these
        // (the blocks are client_hidden) — we are the agent, so the answers are
        // known up front and are waiting for staff at ImmiAccount time.
        const applicantFormId = pickFormId(visaSubclass, false, variant);
        if (applicantFormId) await seedAgentPrefill(intakeForm.id, applicantFormId, { variant });
      }

      // Best-effort: pull the client's GoHighLevel context (Enquiry Notes,
      // source, attribution, etc.) and stash it on the matter row. Failure here
      // doesn't fail matter creation; the Background-from-sales panel just
      // doesn't render. Re-pull is available from the matter detail page.
      void pullGhlForMatter(matter.id, trimmedEmail);

      await refresh();
      setActiveMatterId(matter.id);
      navigate(`/matters/${matter.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create matter');
    } finally {
      setSubmitting(false);
    }
  };

  const resetClient = () => {
    setClientResolved(null);
    setClientEmail('');
    setClientGivenNames('');
    setClientFamilyName('');
    setClientPhone('');
    setClientLookupState('idle');
  };
  const resetSponsor = () => {
    setSponsorResolved(null);
    setSponsorEmail('');
    setSponsorGivenNames('');
    setSponsorFamilyName('');
    setSponsorPhone('');
    setSponsorLookupState('idle');
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex items-center gap-2 text-sm">
        <Link to="/matters" className="text-slate-500 hover:text-[#0B2341] inline-flex items-center gap-1">
          <ChevronLeft size={14} /> All matters
        </Link>
        <span className="text-slate-300">/</span>
        <span className="font-medium text-[#0B2341]">Create matter</span>
      </div>

      <div>
        <h1 className="text-2xl font-bold text-[#0B2341]">Create a new matter</h1>
        <p className="text-sm text-slate-600 mt-1">
          Start by typing the client's email. If we already know them you can attach a new matter
          to their existing record.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="card p-6 space-y-6">
        <FormSection title="Client" subtitle="The person applying for the visa.">
          <ContactSearchBlock
            email={clientEmail}
            onEmailChange={setClientEmail}
            givenNames={clientGivenNames}
            onGivenNamesChange={setClientGivenNames}
            familyName={clientFamilyName}
            onFamilyNameChange={setClientFamilyName}
            phone={clientPhone}
            onPhoneChange={setClientPhone}
            resolved={clientResolved}
            lookupState={clientLookupState}
            onReset={resetClient}
            placeholder="client@example.com"
            kind="applicant"
            disabled={!!prefilledContactId}
          />
        </FormSection>

        {/* Everything below is gated on client identification — keeps the form linear. */}
        <div
          className={`space-y-6 transition-opacity ${
            clientIdentified ? 'opacity-100' : 'opacity-40 pointer-events-none'
          }`}
          aria-hidden={!clientIdentified}
        >
          <FormSection title="Visa" subtitle="What they're applying for. Drives which intake form spawns.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Visa subclass <Required /></label>
                <select
                  value={visaSubclass}
                  onChange={(e) => setVisaSubclass(e.target.value)}
                  className="form-select"
                  required
                >
                  {VISA_OPTIONS.map((v) => (
                    <option key={v.value} value={v.value}>{v.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Relationship / variant <Required /></label>
                <select
                  value={variant}
                  onChange={(e) => setVariant(e.target.value)}
                  className="form-select"
                  required
                >
                  {visaOption.variants.map((v) => (
                    <option key={v} value={v}>{capitalise(v)}</option>
                  ))}
                </select>
                <p className="form-hint">Branches the intake (e.g., NOIM required for engaged 300 PMV).</p>
              </div>
            </div>
          </FormSection>

          {visaOption.sponsorLabel && (
            <FormSection
              title={visaOption.sponsorLabel}
              subtitle={
                visaOption.sponsorHelper ??
                "They'll get their own welcome email and login to fill in their side of the application."
              }
            >
              <ContactSearchBlock
                email={sponsorEmail}
                onEmailChange={setSponsorEmail}
                givenNames={sponsorGivenNames}
                onGivenNamesChange={setSponsorGivenNames}
                familyName={sponsorFamilyName}
                onFamilyNameChange={setSponsorFamilyName}
                phone={sponsorPhone}
                onPhoneChange={setSponsorPhone}
                resolved={sponsorResolved}
                lookupState={sponsorLookupState}
                onReset={resetSponsor}
                placeholder={`${sponsorNoun}@example.com`}
                kind="sponsor"
                roleNoun={sponsorNoun}
                required={sponsorRequired}
              />
            </FormSection>
          )}

          <FormSection title="Internal" subtitle="Workflow details. Visible to staff only.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Assigned case manager</label>
                <select
                  value={assignedStaffId}
                  onChange={(e) => setAssignedStaffId(e.target.value)}
                  className="form-select"
                >
                  <option value="">Unassigned</option>
                  {staffList.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.given_names} {s.family_name} ({s.email})
                    </option>
                  ))}
                </select>
                <p className="form-hint">Defaults to you. Reassign as needed.</p>
              </div>
              <div>
                <label className="form-label">Initial payment date</label>
                <input
                  type="date"
                  value={initialPaymentDate}
                  onChange={(e) => setInitialPaymentDate(e.target.value)}
                  className="form-input"
                />
                <p className="form-hint">When the deposit was paid. Drives some workflow timers.</p>
              </div>
            </div>

            <div className="mt-4">
              <label className="form-label">Internal notes</label>
              <textarea
                value={internalNotes}
                onChange={(e) => setInternalNotes(e.target.value)}
                className="form-textarea"
                rows={3}
                placeholder="Anything the team should know about this matter at a glance."
              />
              <p className="form-hint">Optional. Saved to the matter and visible to staff on the Overview tab. Not a full notes thread (that's coming with matter detail tabs). This is a single note captured at creation.</p>
            </div>
          </FormSection>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-100">
          <Link to="/matters" className="btn-secondary">Cancel</Link>
          <button
            type="submit"
            disabled={submitting || !clientIdentified || !sponsorIdentified}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Creating...' : (
              <>
                <CheckCircle2 size={16} /> Create matter
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

function ContactSearchBlock({
  email,
  onEmailChange,
  givenNames,
  onGivenNamesChange,
  familyName,
  onFamilyNameChange,
  phone,
  onPhoneChange,
  resolved,
  lookupState,
  onReset,
  placeholder,
  kind,
  roleNoun,
  disabled = false,
  required = false,
}: {
  email: string;
  onEmailChange: (v: string) => void;
  givenNames: string;
  onGivenNamesChange: (v: string) => void;
  familyName: string;
  onFamilyNameChange: (v: string) => void;
  phone: string;
  onPhoneChange: (v: string) => void;
  resolved: ResolvedContact | null;
  lookupState: 'idle' | 'searching' | 'found' | 'new';
  onReset: () => void;
  placeholder: string;
  kind: 'applicant' | 'sponsor';
  /** Client-facing noun for the second party ('sponsor' or 'inviter'). */
  roleNoun?: string;
  disabled?: boolean;
  required?: boolean;
}) {
  // Applicant is always required; the second party is required when the caller
  // says so (sponsor-required visas). Drives the HTML `required` attributes
  // below, and whether the asterisks render at all. An optional Inviter must
  // not be marked required, or the browser blocks submit on an empty block.
  const fieldsRequired = kind === 'applicant' || required;
  const noun = kind === 'applicant' ? 'applicant' : roleNoun ?? 'sponsor';
  return (
    <div className="space-y-3">
      <div>
        <label className="form-label">
          Email {fieldsRequired && <Required />}
        </label>
        <div className="relative">
          <input
            type="email"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            className="form-input pr-10"
            placeholder={placeholder}
            required={fieldsRequired}
            autoFocus={kind === 'applicant' && !disabled}
            disabled={disabled || !!resolved}
          />
          {lookupState === 'searching' && (
            <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />
          )}
        </div>
        {kind === 'applicant' && (
          <p className="form-hint">This is what they'll use to sign in to the portal.</p>
        )}
      </div>

      {/* "Found existing" card */}
      {resolved && (
        <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50/40 p-3 flex items-start gap-3">
          <UserCheck size={18} className="text-emerald-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0 text-sm">
            <div className="font-semibold text-emerald-900">
              Existing {resolved.contact_type} found:{' '}
              {[resolved.given_names, resolved.family_name].filter(Boolean).join(' ') || resolved.email}
            </div>
            <div className="text-xs text-emerald-800 mt-0.5">
              {resolved.active_matter_count}{' '}
              {resolved.active_matter_count === 1 ? 'matter' : 'matters'} on file
              {resolved.phone && <> · {resolved.phone}</>}
            </div>
            <div className="mt-2 flex items-center gap-3 text-xs">
              <Link
                to={`/contacts/${resolved.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-700 hover:underline inline-flex items-center gap-1"
              >
                <ExternalLink size={11} /> View profile
              </Link>
              {!disabled && (
                <button
                  type="button"
                  onClick={onReset}
                  className="text-slate-500 hover:underline"
                >
                  Use a different {kind === 'applicant' ? 'client' : noun}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* "New contact" expander — only shown when there's no resolved match */}
      {!resolved && lookupState === 'new' && (
        <div className="rounded-lg border-2 border-slate-200 bg-slate-50/40 p-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
            <UserPlus size={16} /> New {noun} - fill in their details
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="form-label">Given names {fieldsRequired && <Required />}</label>
              <input
                type="text"
                value={givenNames}
                onChange={(e) => onGivenNamesChange(e.target.value)}
                className="form-input"
                required={fieldsRequired}
              />
            </div>
            <div>
              <label className="form-label">Family name {fieldsRequired && <Required />}</label>
              <input
                type="text"
                value={familyName}
                onChange={(e) => onFamilyNameChange(e.target.value)}
                className="form-input"
                required={fieldsRequired}
              />
            </div>
            <div className="md:col-span-2">
              <label className="form-label">Phone</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => onPhoneChange(e.target.value)}
                className="form-input"
                placeholder="+61 ..."
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FormSection({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-4">
        <h2 className="text-base font-semibold text-[#0B2341]">{title}</h2>
        <p className="text-xs text-slate-500">{subtitle}</p>
      </div>
      {children}
    </div>
  );
}

function Required() {
  return <span className="text-red-500" aria-label="required">*</span>;
}

// Format guards so a mistyped email (e.g. "ooutlook.com") or phone never creates
// a matter, which previously sent the welcome email to the wrong person.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[\d\s()-]{7,}$/;

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

const PARTNER_VISA_SUBCLASSES = new Set(['820', '801', '309', '100', '300']);

function pickIntakeForm(visaSubclass: string): { form_code: string; form_version: string } {
  if (PARTNER_VISA_SUBCLASSES.has(visaSubclass)) {
    return { form_code: '47SP-applicant', form_version: '2026.04' };
  }
  if (visaSubclass === '600-tourist') return { form_code: '1419-visitor-tourist', form_version: '2026.04' };
  if (visaSubclass === '600-family') return { form_code: '1418-visitor-family', form_version: '2026.04' };
  if (visaSubclass === '461') return { form_code: '147-461-nz-family', form_version: '2026.04' };
  if (visaSubclass === '020') return { form_code: '1006-bridging-visa-b', form_version: '2026.04' };
  return { form_code: '47SP-applicant', form_version: '2026.04' };
}

async function pullGhlForMatter(matterId: string, email: string): Promise<void> {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const jwt = sessionData.session?.access_token;
    if (!jwt) return;
    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
    await fetch(`${SUPABASE_URL}/functions/v1/ghl-pull-contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
      body: JSON.stringify({ matter_id: matterId, email }),
    });
  } catch (err) {
    console.warn('GHL pull failed (matter still created):', err);
  }
}

// --- Helpers ---------------------------------------------------------------

async function loadResolvedContact(contactId: string): Promise<ResolvedContact | null> {
  const { data } = await supabase
    .from('contacts')
    .select('id, email, given_names, family_name, phone, contact_type')
    .eq('id', contactId)
    .maybeSingle();
  if (!data) return null;
  const { count } = await supabase
    .from('matters')
    .select('id', { count: 'exact', head: true })
    .or(`primary_contact_id.eq.${contactId},sponsor_contact_id.eq.${contactId}`)
    .is('deleted_at', null);
  return { ...data, active_matter_count: count ?? 0 } as ResolvedContact;
}

/**
 * Debounced lookup of a contact by email. Sets resolved + lookup state on the
 * caller's setters. Pass null setters to disable (used when contact is locked
 * via URL param). Skips lookup when email is too short or visibly invalid.
 */
function useDebouncedLookup(
  email: string,
  setResolved: ((c: ResolvedContact | null) => void) | null,
  setLookupState: ((s: 'idle' | 'searching' | 'found' | 'new') => void) | null,
  resolvedNow: ResolvedContact | null,
) {
  // Track whether the email matches the currently-resolved contact's email so
  // editing the locked-in contact's email doesn't kick off another lookup.
  const matchesResolved = useMemo(
    () => !!resolvedNow && resolvedNow.email.toLowerCase() === email.trim().toLowerCase(),
    [resolvedNow, email],
  );

  useEffect(() => {
    if (!setResolved || !setLookupState) return;
    if (matchesResolved) return;
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@') || !trimmed.includes('.')) {
      setLookupState('idle');
      setResolved(null);
      return;
    }
    setLookupState('searching');
    const handle = window.setTimeout(async () => {
      const { data } = await supabase
        .from('contacts')
        .select('id, email, given_names, family_name, phone, contact_type')
        .eq('email', trimmed)
        .maybeSingle();
      if (!data) {
        setResolved(null);
        setLookupState('new');
        return;
      }
      const { count } = await supabase
        .from('matters')
        .select('id', { count: 'exact', head: true })
        .or(`primary_contact_id.eq.${data.id},sponsor_contact_id.eq.${data.id}`)
        .is('deleted_at', null);
      setResolved({ ...data, active_matter_count: count ?? 0 } as ResolvedContact);
      setLookupState('found');
    }, 300);
    return () => window.clearTimeout(handle);
  }, [email, matchesResolved, setResolved, setLookupState]);
}
